/* ============================================================
   AK Spotlight — deep-cut introduction controller

   Surfaces one overlooked product (from the chosen collection)
   at a well-judged moment. Everything about WHEN it appears is
   controlled from Theme settings > Spotlight and arrives here as
   data-* attributes on #AkSpotlight:

     - data-trigger:        both | dwell | scroll | exit
     - data-dwell-seconds:  pause before showing
     - data-scroll:         scroll-depth %% before showing
     - data-returning-only: 1 = stay silent on a first session
     - data-cooldown-hours: minimum gap between appearances
     - data-dismiss-days:   pause after the visitor closes it

   It never introduces a product the visitor is currently viewing
   or has recently viewed, and shows at most once per session.

   Storage is wrapped in try/catch — Safari private mode throws on
   access, and a missing store should silently disable the card,
   never break the page. No backdrop, no scroll lock: this is an
   aside, not a modal.
   ============================================================ */
(function () {
  if (window.__akSpotlightInit) return;
  window.__akSpotlightInit = true;

  var RECENT_MAX = 20;

  var STATE_KEY = 'ak_spotlight_state';
  var RECENT_KEY = 'ak_recent_products';
  var SESSION_KEY = 'ak_spotlight_shown_session';
  var SESSION_MARK = 'ak_spotlight_session_mark';
  var SESS_COUNT_KEY = 'ak_spotlight_sessions';

  var el = null;
  var cfg = null;
  var fired = false;
  var atcVisible = false;   /* sticky add-to-cart bar currently on screen? */
  var bandVisible = false;  /* footer newsletter band currently on screen? */

  /* Preview override: add ?spotlight=preview to any URL (or the
     #spotlight-preview hash) to force the card to appear immediately,
     bypassing every frequency cap and the engagement trigger. It does
     NOT record a "shown" timestamp, so it won't start a cooldown —
     handy for design/QA in the theme editor or a normal browser. */
  var PREVIEW = false;
  try {
    PREVIEW = /[?&]spotlight=preview(\b|&|$)/.test(window.location.search) ||
      window.location.hash === '#spotlight-preview';
  } catch (e) { PREVIEW = false; }

  /* ── Safe storage helpers ─────────────────────────────── */

  function lsGet(key) {
    try { return window.localStorage.getItem(key); } catch (e) { return null; }
  }
  function lsSet(key, val) {
    try { window.localStorage.setItem(key, val); } catch (e) { /* noop */ }
  }
  function ssGet(key) {
    try { return window.sessionStorage.getItem(key); } catch (e) { return null; }
  }
  function ssSet(key, val) {
    try { window.sessionStorage.setItem(key, val); } catch (e) { /* noop */ }
  }
  function readJSON(raw, fallback) {
    if (!raw) return fallback;
    try { return JSON.parse(raw); } catch (e) { return fallback; }
  }
  function num(val, fallback) {
    var n = parseFloat(val);
    return isNaN(n) ? fallback : n;
  }

  /* ── Read settings off the element ────────────────────── */

  function readConfig() {
    var d = el.dataset || {};
    return {
      currentHandle: d.currentHandle || '',
      eyebrow: d.eyebrow || '',
      trigger: d.trigger || 'both',
      dwellMs: num(d.dwellSeconds, 30) * 1000,
      scrollPct: num(d.scroll, 55),
      returningOnly: d.returningOnly === '1',
      cooldownMs: num(d.cooldownHours, 48) * 60 * 60 * 1000,
      dismissMs: num(d.dismissDays, 14) * 24 * 60 * 60 * 1000,
      signupReady: d.signupReady === '1',
      signupAllowedHere: d.signupAllowedHere === '1',
      productAllowedHere: d.productAllowedHere === '1',
      signupMinMs: num(d.signupMinSeconds, 20) * 1000,
      knownCustomer: d.knownCustomer === '1',
      klCompany: d.klCompany || '',
      klList: d.klList || '',
      toolAllowedHere: d.toolAllowedHere === '1',
      toolUrl: d.toolUrl || ''
    };
  }

  /* ── Which card: sign-up or product ───────────────────────
     Sign-up is for people who are not on the list yet. Everyone
     else gets a product, because asking a subscriber to subscribe
     is noise and wastes the one slot we get.

     Three signals, cheapest first, none of them requiring a network
     call. None PROVES list membership; all three err toward not
     pestering, which is the right direction to be wrong in.
       1. Signed-in Shopify customer (from Liquid)
       2. Our own flag, set when they subscribe here or in the footer
       3. Klaviyo's onsite script reporting an identified visitor    */

  var SUBSCRIBED_KEY = 'ak_subscribed';
  /* Set when the SIGN-UP card is closed. Distinct from the ordinary
     dismissal cooldown: closing the sign-up means "not joining", not
     "show me nothing ever". It retires sign-up permanently and hands
     the slot to the product surfacer. Added 2026-08-05. */
  var SIGNUP_DISMISSED_KEY = 'ak_signup_dismissed';

  /* ONLY AN EXPLICIT BOOLEAN TRUE COUNTS.
     -------------------------------------------------------------
     klaviyo.isIdentified() does NOT return a boolean despite the name.
     On this store it returns {} — and !!{} is true, so the old
     implementation classified EVERY visitor as already subscribed.
     Sign-up was therefore skipped site-wide and the product surfacer
     won every slot, which is what the blog session recordings showed.
     Found 2026-08-06.

     Newer Klaviyo builds return a Promise here, which is also an object
     and would fail the same way. So: trust the value only when it is
     literally boolean, and treat every other shape as "unknown", which
     resolves to not-subscribed and therefore to showing sign-up.

     Being wrong in that direction costs one redundant ask. Being wrong
     the other way costs every sign-up on the site. */
  function klaviyoIdentified() {
    try {
      if (window.klaviyo && typeof window.klaviyo.isIdentified === 'function') {
        var v = window.klaviyo.isIdentified();
        if (typeof v === 'boolean') return v;
        /* Object, Promise, undefined: unusable. Fall through. */
      }
      /* Legacy queue API: only an actual email address counts. */
      if (window._learnq && typeof window._learnq.identify === 'function') {
        var ids = window._learnq.identify();
        if (ids && typeof ids === 'object') {
          return !!(ids.$email || ids.email);
        }
      }
    } catch (e) { /* noop */ }
    return false;
  }

  function looksSubscribed() {
    if (cfg.knownCustomer) return true;
    if (lsGet(SUBSCRIBED_KEY) === '1') return true;
    return klaviyoIdentified();
  }

  function markSubscribed() {
    lsSet(SUBSCRIBED_KEY, '1');
  }

  function wantsSignup() {
    /* Template allow list first: sign-up never appears on a product page
       or in the cart, whatever the settings say. An interruption there
       costs an order; a missed sign-up costs one address. */
    if (!cfg.signupAllowedHere) return false;
    if (lsGet(SIGNUP_DISMISSED_KEY) === '1') return false;  /* asked, declined */
    return cfg.signupReady && !looksSubscribed();
  }

  /* ── Tie pairing tool promotion ─────────────────────────
     Shown once per visitor, ever. The flag is set the moment the card
     appears (seen, not clicked, is the test) and by the snippet whenever
     someone lands on the tool page. Added 2026-09-27. */
  var TOOL_SEEN_KEY = 'ak_tool_seen';

  function wantsTool() {
    if (!cfg.toolAllowedHere || !cfg.toolUrl) return false;
    return lsGet(TOOL_SEEN_KEY) !== '1';
  }

  /* ── Session counting (for "returning visitors only") ──── */

  function sessionCount() {
    if (!ssGet(SESSION_MARK)) {
      ssSet(SESSION_MARK, '1');
      var c = parseInt(lsGet(SESS_COUNT_KEY) || '0', 10) + 1;
      lsSet(SESS_COUNT_KEY, String(c));
    }
    return parseInt(lsGet(SESS_COUNT_KEY) || '0', 10);
  }

  /* ── Recently-viewed tracking ─────────────────────────── */

  function recordCurrentProduct(currentHandle) {
    if (!currentHandle) return;
    var list = readJSON(lsGet(RECENT_KEY), []);
    list = list.filter(function (h) { return h !== currentHandle; });
    list.unshift(currentHandle);
    if (list.length > RECENT_MAX) list = list.slice(0, RECENT_MAX);
    lsSet(RECENT_KEY, JSON.stringify(list));
  }

  /* ── Frequency gate ───────────────────────────────────── */

  function allowedToShow(sessions) {
    if (ssGet(SESSION_KEY)) return false;            /* already shown this session */
    if (cfg.returningOnly && sessions < 2) return false; /* first session: stay quiet */
    var state = readJSON(lsGet(STATE_KEY), {});
    var now = Date.now();
    if (state.dismissedAt && now - state.dismissedAt < cfg.dismissMs) return false;
    if (state.shownAt && now - state.shownAt < cfg.cooldownMs) return false;
    return true;
  }

  function markShown() {
    ssSet(SESSION_KEY, '1');
    var state = readJSON(lsGet(STATE_KEY), {});
    state.shownAt = Date.now();
    lsSet(STATE_KEY, JSON.stringify(state));
  }

  function markDismissed() {
    var state = readJSON(lsGet(STATE_KEY), {});
    state.dismissedAt = Date.now();
    lsSet(STATE_KEY, JSON.stringify(state));
  }

  /* ── Choose which product to introduce ────────────────── */

  function chooseProduct(items, currentHandle) {
    if (!items || !items.length) return null;
    var recent = readJSON(lsGet(RECENT_KEY), []);
    var exclude = {};
    if (currentHandle) exclude[currentHandle] = true;
    recent.forEach(function (h) { exclude[h] = true; });

    var eligible = items.filter(function (it) { return it.handle && !exclude[it.handle]; });
    /* If everything has been seen, fall back to anything but the
       product on screen — better a familiar deep cut than nothing. */
    if (!eligible.length) {
      eligible = items.filter(function (it) {
        return it.handle && it.handle !== currentHandle;
      });
    }
    if (!eligible.length) return null;
    return eligible[Math.floor(Math.random() * eligible.length)];
  }

  /* ── Populate + reveal ────────────────────────────────── */

  function populate(item) {
    /* All anchors, not one. The media and the title are both
       [data-spotlight-link] since the "See it" CTA was removed
       2026-08-05. */
    var links = el.querySelectorAll('[data-spotlight-link]');
    var img = el.querySelector('[data-spotlight-img]');
    var titleEl = el.querySelector('[data-spotlight-title]');

    for (var i = 0; i < links.length; i++) links[i].href = item.url;
    if (titleEl) titleEl.textContent = item.title;

    if (img) {
      img.src = item.img || '';
      img.alt = cfg.eyebrow ? (cfg.eyebrow + ': ' + item.title) : item.title;
    }

    el.dataset.url = item.url;
  }

  /* Card-wide click. Convenience on top of the real anchors, so the
     whole card is a target now that the CTA is gone. Ignores clicks on
     the close button and on anchors (which handle themselves), and
     ignores modified clicks so open-in-new-tab still behaves. */
  function bindCardClick() {
    var card = el.querySelector('.ak-spotlight__card');
    if (!card || card.dataset.clickBound === '1') return;
    card.dataset.clickBound = '1';
    card.addEventListener('click', function (e) {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      if (e.target.closest('[data-ak-spotlight-close], a, button, input, label, form')) return;
      /* Product card only. On the sign-up panel a stray click must not
         navigate someone away mid-typing. */
      if (el.classList.contains('ak-spotlight--signup')) return;
      var url = el.dataset.url;
      if (url) window.location.href = url;
    });
  }

  /* ── Sign-up panel ────────────────────────────────────── */

  function showPanel(name) {
    var panels = el.querySelectorAll('[data-spotlight-panel]');
    for (var i = 0; i < panels.length; i++) {
      var p = panels[i];
      if (p.getAttribute('data-spotlight-panel') === name) p.removeAttribute('hidden');
      else p.setAttribute('hidden', '');
    }
    /* The card-wide click-to-navigate belongs to the product card only.
       On the sign-up panel a stray click must never yank someone off to
       a product page mid-typing. */
    el.classList.toggle('ak-spotlight--signup', name === 'signup');
    if (name === 'tool') el.dataset.url = cfg.toolUrl;

    /* Bind the footer watcher HERE, not at the call site. It used to be
       wired only where signupMode computed true, so the preview path
       (?spotlight_mode=signup) showed the panel without it and the card
       refused to stand down near the newsletter band. Tying it to the
       panel means every route that opens sign-up gets the behaviour.
       Reported 2026-08-05. */
    if (name === 'signup') watchNewsletterBand();
  }

  function bindSignup() {
    var form = el.querySelector('[data-signup-form]');
    if (!form || form.dataset.bound === '1') return;
    form.dataset.bound = '1';

    var email = el.querySelector('[data-signup-email]');
    var submit = el.querySelector('[data-signup-submit]');
    var errorEl = el.querySelector('[data-signup-error]');
    var ask = el.querySelector('[data-signup-ask]');
    var done = el.querySelector('[data-signup-done]');

    function fail(msg) {
      if (!errorEl) return;
      errorEl.textContent = msg;
      errorEl.removeAttribute('hidden');
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (!email || !email.reportValidity()) return;
      if (form.dataset.sending === '1') return;
      form.dataset.sending = '1';

      var label = submit ? submit.textContent : '';
      if (submit) { submit.disabled = true; submit.textContent = 'Sending…'; }
      if (errorEl) errorEl.setAttribute('hidden', '');

      fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' + encodeURIComponent(cfg.klCompany), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': '2025-10-15' },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              profile: { data: { type: 'profile', attributes: { email: email.value } } }
            },
            relationships: { list: { data: { type: 'list', id: cfg.klList } } }
          }
        })
      })
        .then(function (res) {
          if (res.status === 202) {
            /* Permanent. A subscriber is never asked again, on any page,
               in any session: the flag outlives the cooldown. */
            markSubscribed();
            if (ask) ask.setAttribute('hidden', '');
            if (done) done.removeAttribute('hidden');
            return;
          }
          if (window.console && console.warn) {
            console.warn('[ak-spotlight] Klaviyo returned ' + res.status + ' from ' + location.origin +
              (res.status === 403 ? ' — public key is not allowed on this domain.' : ''));
          }
          fail('That did not go through. Please try again.');
        })
        .catch(function () { fail('Something went wrong. Please try again.'); })
        .finally(function () {
          form.dataset.sending = '0';
          if (submit) { submit.disabled = false; submit.textContent = label; }
        });
    });
  }

  /* ── Don't ask twice on one screen ─────────────────────────
     The footer newsletter band makes the same offer. While it is on
     screen the sign-up card would be asking to join the list twice at
     once, so the card steps aside.

     STEPS ASIDE, does not retire. The first version removed is-visible
     and unmounted the card, so scrolling back up left nothing behind.
     Now it borrows the same .is-suppressed treatment the sticky
     add-to-cart bar already uses: fades out, ignores pointer events,
     stays mounted, comes back when the band leaves view. One mechanism
     for "something more important is on screen", not two.
     See [[feedback-reuse-existing-components]].

     Nothing is recorded either way: stepping aside is not a dismissal,
     and the visitor refused nothing. */
  var bandWatched = false;

  function watchNewsletterBand() {
    if (bandWatched) return;              /* showPanel can run more than once */
    if (!('IntersectionObserver' in window)) return;
    var band = document.querySelector('.ak-newsletter-split__panel, .newsletter-form');
    if (!band) return;
    bandWatched = true;
    new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        bandVisible = entries[i].isIntersecting;
      }
      applySuppression();
    }, { threshold: 0.2 }).observe(band);
  }

  function reveal(skipMark) {
    bindCardClick();
    el.hidden = false;
    el.setAttribute('aria-hidden', 'false');
    /* Force reflow so the transition runs from the hidden state. */
    /* eslint-disable-next-line no-unused-expressions */
    el.offsetHeight;
    el.classList.add('is-visible');
    applySuppression();
    if (!skipMark) markShown();
  }

  function dismiss() {
    el.classList.remove('is-visible');
    el.setAttribute('aria-hidden', 'true');
    /* Closing the SIGN-UP retires sign-up for good and hands the slot to
       the product surfacer, which is a different offer and worth one
       chance. Closing the PRODUCT card is an ordinary dismissal.
       Either way the normal cooldown still applies, so nothing returns
       in this session or before the cooldown expires. */
    if (el.classList.contains('ak-spotlight--signup')) {
      lsSet(SIGNUP_DISMISSED_KEY, '1');
    }
    markDismissed();
    setTimeout(function () {
      if (!el.classList.contains('is-visible')) el.hidden = true;
    }, 600);
  }

  /* ── Sticky add-to-cart coexistence ───────────────────────
     The PDP sticky bar is a <sticky-cart> element that gains the
     class "is-visible" (slides up) once the buy box scrolls off.
     Two fixed cards in the same corner is clutter, and on a PDP
     the buy nudge should win — so we step the spotlight aside
     while the bar is up and bring it back when it retracts. */

  /* Either reason steps the card aside, and it returns only when both
     are clear: the sticky add-to-cart bar, and the footer newsletter
     band (sign-up mode only, since a product card does not compete with
     a newsletter form). */
  function applySuppression() {
    if (!el || !el.classList.contains('is-visible')) return;
    var bandBlocks = bandVisible && el.classList.contains('ak-spotlight--signup');
    el.classList.toggle('is-suppressed', atcVisible || bandBlocks);
  }

  function watchStickyCart() {
    var bar = document.querySelector('sticky-cart');
    if (!bar) return;
    function update() {
      atcVisible = bar.classList.contains('is-visible');
      applySuppression();
    }
    update();
    try {
      var obs = new MutationObserver(update);
      obs.observe(bar, { attributes: true, attributeFilter: ['class'] });
    } catch (e) { /* noop */ }
  }

  /* ── Engagement trigger ───────────────────────────────── */

  function armTrigger(onEngage) {
    var done = false;
    var dwellTimer = null;

    function cleanup() {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('mouseout', onMouseOut);
      if (dwellTimer) clearTimeout(dwellTimer);
    }
    function fire() {
      if (done) return;
      done = true;
      cleanup();
      onEngage();
    }
    function onScroll() {
      var doc = document.documentElement;
      var scrollable = doc.scrollHeight - window.innerHeight;
      if (scrollable <= 0) return;
      var pct = (window.scrollY || doc.scrollTop) / scrollable * 100;
      if (pct >= cfg.scrollPct) fire();
    }
    function onMouseOut(e) {
      /* Exit intent: pointer leaves through the top of the viewport. */
      if (!e.relatedTarget && (e.clientY == null || e.clientY <= 0)) fire();
    }

    var mode = cfg.trigger;
    var noHover = false;
    try { noHover = window.matchMedia('(hover: none)').matches; } catch (e) { noHover = 'ontouchstart' in window; }

    if (mode === 'scroll') {
      window.addEventListener('scroll', onScroll, { passive: true });
    } else if (mode === 'dwell') {
      dwellTimer = setTimeout(fire, cfg.dwellMs);
    } else if (mode === 'exit') {
      if (noHover) {
        /* No exit intent on touch — fall back to a patient dwell so
           mobile visitors still get one well-spaced introduction. */
        dwellTimer = setTimeout(fire, Math.max(cfg.dwellMs, 30000));
      } else {
        document.addEventListener('mouseout', onMouseOut);
      }
    } else {
      /* both: scroll OR dwell, whichever comes first */
      window.addEventListener('scroll', onScroll, { passive: true });
      dwellTimer = setTimeout(fire, cfg.dwellMs);
    }
  }

  /* ── Sign-up trigger: exit intent or a real dwell ──────────
     Deliberately NOT the shared armTrigger. Two differences, both
     protective:

       1. SCROLL NEVER FIRES IT. The shared trigger's default is scroll
          OR dwell, and 55% of a short page can be reached in about
          three seconds. Asking for an email that fast is the version of
          this feature that costs conversion instead of adding to it.

       2. A HARD FLOOR. Nothing shows before signupMinMs, including exit
          intent. A pointer flick toward the tab bar in second two is
          not intent to leave, it is someone reaching for a bookmark.

     Touch devices have no exit intent, so dwell carries them; the floor
     applies there too. */
  function armSignupTrigger(onEngage) {
    var done = false;
    var t0 = Date.now();
    var dwellTimer = null;

    function cleanup() {
      document.removeEventListener('mouseout', onMouseOut);
      if (dwellTimer) clearTimeout(dwellTimer);
    }
    function fire() {
      if (done) return;
      if (Date.now() - t0 < cfg.signupMinMs) return;   /* floor */
      done = true;
      cleanup();
      onEngage();
    }
    function onMouseOut(e) {
      if (!e.relatedTarget && (e.clientY == null || e.clientY <= 0)) fire();
    }

    /* Dwell is the guaranteed path: never shorter than the floor, and
       never shorter than the card's own configured pause. */
    dwellTimer = setTimeout(fire, Math.max(cfg.dwellMs, cfg.signupMinMs));

    var noHover = false;
    try { noHover = window.matchMedia('(hover: none)').matches; } catch (e) { noHover = 'ontouchstart' in window; }
    if (!noHover) document.addEventListener('mouseout', onMouseOut);
  }

  /* ── Init ─────────────────────────────────────────────── */

  function init() {
    el = document.getElementById('AkSpotlight');
    if (!el) return;
    cfg = readConfig();

    recordCurrentProduct(cfg.currentHandle);
    var sessions = sessionCount();

    /* Close is always wired, even if we don't show this time. */
    el.addEventListener('click', function (e) {
      if (e.target.closest('[data-ak-spotlight-close]')) {
        e.preventDefault();
        dismiss();
      }
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && el.classList.contains('is-visible')) dismiss();
    });

    watchStickyCart();

    var dataEl = document.querySelector('[data-spotlight-data]');
    var items = readJSON(dataEl ? dataEl.textContent : null, []);

    /* Decide the card once, up front. Sign-up wins only for visitors
       who do not already look like subscribers; everyone else gets a
       product. Both panels are already in the DOM, so this is a toggle,
       not a fetch. */
    var signupMode = wantsSignup();
    /* Order of the one slot: sign-up for non-subscribers, then the tie
       pairing tool once, then a product. */
    var toolMode = !signupMode && wantsTool();

    if (signupMode) {
      showPanel('signup');   /* wires the footer watcher itself */
      bindSignup();
    } else if (toolMode) {
      showPanel('tool');
    } else {
      showPanel('product');
    }

    /* Preview override: show immediately, ignore caps + trigger, and
       don't record a cooldown. ?spotlight=preview&spotlight_mode=signup
       forces the sign-up panel so it can be designed without clearing
       storage or signing out. */
    if (PREVIEW) {
      var forceSignup = /[?&]spotlight_mode=signup(\b|&|$)/.test(location.search);
      if (/[?&]spotlight_mode=tool(\b|&|$)/.test(location.search) && cfg.toolAllowedHere) {
        showPanel('tool');
        reveal(true);
        return;
      }
      if (forceSignup && cfg.signupReady) {
        showPanel('signup');
        bindSignup();
        reveal(true);
        return;
      }
      if (signupMode || toolMode) { reveal(true); return; }
      var previewPick = chooseProduct(items, cfg.currentHandle) || (items && items[0]);
      if (previewPick) {
        populate(previewPick);
        reveal(true);
      }
      return;
    }

    if (!allowedToShow(sessions)) return;

    /* Sign-up needs no catalogue, so it shows even when the spotlight
       collection is exhausted for this visitor. The product surfacer
       needs both a catalogue AND a template that permits it. */
    var pick = null;
    if (!signupMode && !toolMode) {
      if (!cfg.productAllowedHere) return;
      pick = chooseProduct(items, cfg.currentHandle);
      if (!pick) return;
    }

    if (fired) return;
    fired = true;

    /* Sign-up uses the stricter trigger: exit intent or a real dwell,
       never scroll, never inside the first signupMinMs. */
    var arm = signupMode ? armSignupTrigger : armTrigger;
    arm(function () {
      if (toolMode) lsSet(TOOL_SEEN_KEY, '1');
      else if (!signupMode) populate(pick);
      reveal();
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
